import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import {
  MapPinIcon,
  CalendarDaysIcon,
  ChatBubbleLeftEllipsisIcon,
  ChevronRightIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { RepairAndInquiriesStatusEntity } from '../../../../customEntites/RepairAndInquiries.entity'
import { ServiceRequestItem } from '../../../../api/repairAndInquiries'
import { ContractEntity } from '../../../../entities'
import { GlobalContext } from '../../../../App'
import { ContextEntity } from '../../../../customEntites/context.entity'

const STATUS_PROGRESS: Record<string, number> = {
  'open': 20,
  'in-progress': 60,
  'completed': 100,
}

const STATUS_STYLES: Record<string, { bg: string; text: string; Icon: React.ElementType; barColor: string }> = {
  'open':        { bg: 'bg-orange-50',  text: 'text-orange-500', Icon: ClockIcon,        barColor: '#f97316' },
  'in-progress': { bg: 'bg-[#e8f4fc]',  text: 'text-[#218cda]',  Icon: ArrowPathIcon,    barColor: '#218cda' },
  'completed':   { bg: 'bg-green-50',   text: 'text-green-600',  Icon: CheckCircleIcon,  barColor: '#16a34a' },
}

export const RequestSummary = ({
  request,
  statuses,
}: {
  request: ServiceRequestItem,
  statuses: RepairAndInquiriesStatusEntity[],
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { context }: ContextEntity = useContext(GlobalContext)

  const requestStatus = statuses.find((s) => s?.nameAims?.includes(request?.status))
  const statusKey = requestStatus?.name?.toLowerCase().replace(' ', '-') ?? 'open'
  const progressPercent = STATUS_PROGRESS[statusKey] ?? 20
  const statusStyle = STATUS_STYLES[statusKey] ?? STATUS_STYLES['open']
  const StatusIcon = statusStyle.Icon
  const lang = localStorage.getItem('SELECTED_LNG')
  const isUrgent = request?.urgency === 'Urgent'

  const descriptionSnippet = request?.description
    ?.replace(/<[^>]*>/g, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120)

  const activeContract = context?.contracts?.activeContracts?.find(
    (c: ContractEntity) =>
      c.contractNumber === request?.contract_number?.replace('/', '-') ||
      c.contractNumber === request?.contract_number
  )

  const messageCount = request?.messageCount ?? request?.user_comment?.length ?? 0

  return (
    <button
      className={`w-full text-left bg-white rounded-2xl border transition-shadow hover:shadow-md mb-4 overflow-hidden ${
        isUrgent ? 'border-red-300' : 'border-gray-200'
      } shadow-sm`}
      style={{ fontFamily: '"Red Hat Display", sans-serif', fontSize: '14px' }}
      onClick={() => navigate(`/repairs-and-inquiries/${request.code}`)}
    >
      <div className='px-6 pt-5 pb-5'>
        {/* Top row */}
        <div className='flex items-center gap-2 mb-3 flex-wrap'>
          <span className='text-sm font-semibold' style={{ color: '#218cda' }}>{request?.ticketnr ?? request?.code}</span>

          {requestStatus && (
            <span className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
              <StatusIcon className='w-3.5 h-3.5' />
              {lang === 'EN' ? requestStatus.name : requestStatus.nameDe}
            </span>
          )}

          {request?.main_issue && (
            <span className='px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full'>
              {request.main_issue}
            </span>
          )}

          {isUrgent && (
            <span className='flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-500 text-xs font-medium rounded-full'>
              <ExclamationTriangleIcon className='w-3.5 h-3.5' />
              {t('REPAIR_AND_INQUIRIES.URGENT')}
            </span>
          )}

          <ChevronRightIcon className='w-4 h-4 text-gray-400 ml-auto shrink-0' />
        </div>

        {/* Title */}
        <h4
          className='mb-1'
          style={{ color: '#212121', fontFamily: '"Red Hat Display"', fontSize: '1rem', fontWeight: 500, lineHeight: '22.08px' }}
        >{request?.subject}</h4>

        {/* Description */}
        {descriptionSnippet && (
          <p className='mb-4 line-clamp-2' style={{ color: '#9E9E9E', fontSize: '12.16px', lineHeight: '18.24px' }}>{descriptionSnippet}</p>
        )}

        {/* Progress bar */}
        <div className='flex items-center gap-3 mb-4'>
          <div className='flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden'>
            <div
              className='h-1.5 rounded-full'
              style={{ backgroundColor: statusStyle.barColor, width: `${progressPercent}%` }}
            />
          </div>
          <span className='text-xs text-gray-400 shrink-0'>{progressPercent}% complete</span>
        </div>

        {/* Footer */}
        <div className='flex items-center gap-5 flex-wrap'>
          {activeContract?.address && (
            <span className='flex items-center gap-1.5' style={{ color: '#9E9E9E', fontFamily: '"Red Hat Display"', fontSize: '0.75rem', fontWeight: 500, lineHeight: '18.24px' }}>
              <MapPinIcon className='w-3.5 h-3.5 shrink-0' />
              {activeContract.address}
            </span>
          )}
          <span className='flex items-center gap-1.5' style={{ color: '#9E9E9E', fontFamily: '"Red Hat Display"', fontSize: '0.75rem', fontWeight: 500, lineHeight: '18.24px' }}>
            <CalendarDaysIcon className='w-3.5 h-3.5 shrink-0' />
            {moment(request?.create_date).format('DD MMM YYYY')}
          </span>
          <span className='flex items-center gap-1.5' style={{ color: '#9E9E9E', fontFamily: '"Red Hat Display"', fontSize: '0.75rem', fontWeight: 500, lineHeight: '18.24px' }}>
            <ChatBubbleLeftEllipsisIcon className='w-3.5 h-3.5 shrink-0' />
            {t(messageCount === 1 ? 'REPAIR_AND_INQUIRIES.MESSAGE_COUNT_ONE' : 'REPAIR_AND_INQUIRIES.MESSAGE_COUNT_OTHER', { count: messageCount })}
          </span>
        </div>
      </div>
    </button>
  )
}
